import type { FormField } from '../../../types';
import { normalizeConversationSections, standaloneFormBlock, type ConversationSection, type FormBlock } from '../../../lib/conversation-sections';

/** Fields and grouping metadata are committed together. */
export interface BuilderDefinition { fields: FormField[]; sections: ConversationSection[] }

/** Inclusive bounds keep an Information block and its attached answer inseparable. */
export function builderFieldBounds(fields: readonly FormField[], index: number): [number, number] {
  const field = fields[index];
  if (field?.informationFieldId && fields[index - 1]?.id === field.informationFieldId && fields[index - 1]?.type === 'information') return [index - 1, index];
  if (field?.type === 'information' && fields[index + 1]?.informationFieldId === field.id) return [index, index + 1];
  return [index, index];
}

/** Find the canvas block that owns any of its member questions. */
function blockForField(section: ConversationSection | undefined, fieldId: string): FormBlock | undefined {
  return section?.blocks?.find(block => block.fieldIds.includes(fieldId));
}

/** Inclusive visible bounds, for a nested form or Information/answer pair. */
export function builderBlockBounds(fields: readonly FormField[], sections: readonly ConversationSection[], index: number): [number, number] {
  const id = fields[index]?.id;
  const block = id && blockForField(sections.find(section => section.fieldIds.includes(id)), id);
  if (block) {
    const ids = block.fieldIds;
    const start = fields.findIndex(field => field.id === ids[0]);
    if (start >= 0 && ids.every((member, offset) => fields[start + offset]?.id === member)) return [start, start + ids.length - 1];
  }
  return builderFieldBounds(fields, index);
}

/** Explicit null denotes an ungrouped plus; synthetic owners only contain their own block. */
function insertionOwner(fields: readonly FormField[], sections: readonly ConversationSection[], position: number, ownerId?: string | null): ConversationSection | undefined {
  const owner = ownerId === undefined ? sections.find(section => section.fieldIds.includes(fields[position - 1]?.id))
    : sections.find(section => section.id === ownerId);
  return owner && !standaloneFormBlock(owner) ? owner : undefined;
}

/** Omit empty block metadata when the last block leaves an authored section. */
function withBlocks(section: ConversationSection, blocks: FormBlock[]): ConversationSection {
  const { blocks: _old, ...metadata } = section;
  return blocks.length ? { ...metadata, blocks } : metadata;
}

/** A standalone form still owns its copied fields so its conditions and source response survive. */
function standaloneOwner(block: FormBlock): ConversationSection {
  return { id: `nested_${block.id}`, title: block.title, fieldIds: [...block.fieldIds], blocks: [block] };
}

/** Remove a section heading, preserving the order and identity of all its nested forms. */
export function ungroupBuilderSection(sections: readonly ConversationSection[], id: string): ConversationSection[] {
  return sections.flatMap(section => {
    if (section.id !== id) return [section];
    const blocks = [...section.blocks ?? []];
    blocks.sort((left, right) => section.fieldIds.indexOf(left.fieldIds[0]) - section.fieldIds.indexOf(right.fieldIds[0]));
    return blocks.map(standaloneOwner);
  });
}

/** Preserve empty headings at their existing location when the last field is removed. */
export function reconcileBuilderSections(previousFields: readonly FormField[], fields: readonly FormField[], sections: readonly ConversationSection[]): ConversationSection[] {
  const ids = new Set(fields.map(field => field.id));
  return normalizeConversationSections(fields, sections.map(section => {
    const fieldIds = section.fieldIds.filter(id => ids.has(id));
    const { beforeFieldId: _anchor, ...metadata } = section;
    if (fieldIds.length) return { ...metadata, fieldIds };
    const originalIndex = previousFields.findIndex(field => field.id === (section.fieldIds[0] ?? section.beforeFieldId));
    const beforeFieldId = originalIndex < 0 ? undefined : previousFields.slice(originalIndex).find(field => ids.has(field.id) && !section.fieldIds.includes(field.id))?.id;
    return { ...metadata, fieldIds, ...(beforeFieldId ? { beforeFieldId } : {}) };
  }));
}

/** Insert new questions into the plus control's owner, including an empty section. */
export function insertSectionFields(fields: readonly FormField[], sections: readonly ConversationSection[], insertIndex: number, additions: FormField[], ownerId?: string | null): BuilderDefinition {
  const [, end] = builderBlockBounds(fields, sections, insertIndex - 1);
  const position = Math.min(fields.length, Math.max(0, end >= insertIndex ? end + 1 : insertIndex));
  const next = [...fields]; next.splice(position, 0, ...additions);
  const owner = insertionOwner(fields, sections, position, ownerId)?.id;
  const nextSections = sections.map(section => section.id === owner ? { ...section, fieldIds: [...section.fieldIds, ...additions.map(field => field.id)] } : section);
  return { fields: next, sections: normalizeConversationSections(next, nextSections) };
}

/** A section begins at the chosen plus and owns the following questions in that group. */
export function addBuilderSection(fields: readonly FormField[], sections: readonly ConversationSection[], insertIndex: number, id: string, ownerId?: string | null): BuilderDefinition {
  const [, end] = builderBlockBounds(fields, sections, insertIndex - 1);
  const position = Math.min(fields.length, Math.max(0, end >= insertIndex ? end + 1 : insertIndex));
  const owner = insertionOwner(fields, sections, position, ownerId);
  const membership = new Set(sections.flatMap(section => section.fieldIds));
  const suffix = owner ? fields.slice(position).filter(field => owner.fieldIds.includes(field.id))
    : fields.slice(position, fields.findIndex((field, i) => i >= position && membership.has(field.id)) < 0 ? fields.length : fields.findIndex((field, i) => i >= position && membership.has(field.id)));
  const suffixIds = new Set(suffix.map(field => field.id));
  const transferred = owner?.blocks?.filter(block => suffixIds.has(block.fieldIds[0])) ?? [];
  const section = withBlocks({ id, title: 'Untitled section', fieldIds: [...suffixIds], ...(!suffix.length && fields[position] ? { beforeFieldId: fields[position].id } : {}) }, transferred);
  const nextSections = sections.map(candidate => {
    if (candidate.id !== owner?.id) return candidate;
    const fieldIds = candidate.fieldIds.filter(fieldId => !suffixIds.has(fieldId));
    return withBlocks({ ...candidate, fieldIds, ...(!fieldIds.length && fields[position] ? { beforeFieldId: fields[position].id } : {}) },
      (candidate.blocks ?? []).filter(block => !transferred.includes(block)));
  });
  nextSections.splice(owner ? nextSections.findIndex(candidate => candidate.id === owner.id) + 1 : nextSections.length, 0, section);
  return { fields: [...fields], sections: normalizeConversationSections(fields, nextSections) };
}

/** Move the complete question group to an explicit section boundary on release. */
export function moveSectionFields(fields: readonly FormField[], sections: readonly ConversationSection[], fieldId: string, targetSectionId?: string | null, beforeFieldId?: string): BuilderDefinition {
  const index = fields.findIndex(field => field.id === fieldId);
  if (index < 0) return { fields: [...fields], sections: [...sections] };
  const [start, end] = builderBlockBounds(fields, sections, index);
  const moved = fields.slice(start, end + 1); const movedIds = new Set(moved.map(field => field.id));
  const sourceOwner = sections.find(section => section.fieldIds.includes(fieldId));
  const block = blockForField(sourceOwner, fieldId);
  const destination = sections.find(section => section.id === targetSectionId);
  const target = destination && !standaloneFormBlock(destination) ? destination : undefined;
  if (block && moved.length !== block.fieldIds.length) return { fields: [...fields], sections: [...sections] };
  if (beforeFieldId && movedIds.has(beforeFieldId) && (sourceOwner?.id === target?.id || sourceOwner && standaloneFormBlock(sourceOwner) && !target)) {
    return { fields: [...fields], sections: [...sections] };
  }
  let boundary = beforeFieldId;
  if (!boundary && destination) {
    const lastIndex = Math.max(...destination.fieldIds.map(id => fields.findIndex(field => field.id === id)), -1);
    boundary = lastIndex >= 0 ? fields[lastIndex + 1]?.id : destination.beforeFieldId;
  }
  if (boundary && movedIds.has(boundary)) boundary = fields[end + 1]?.id;
  const next = fields.filter(field => !movedIds.has(field.id));
  let position = boundary ? next.findIndex(field => field.id === boundary) : next.length;
  if (position < 0) position = next.length;
  if (boundary) position = builderBlockBounds(next, sections, position)[0];
  if (target) {
    const indices = next.flatMap((field, index) => target.fieldIds.includes(field.id) ? [index] : []);
    if (indices.length) position = Math.max(indices[0], Math.min(indices[indices.length - 1] + 1, position));
  } else if (next[position]) {
    const neighbor = sections.find(section => section.fieldIds.includes(next[position].id));
    if (neighbor) position = next.findIndex(field => neighbor.fieldIds.includes(field.id));
  }
  next.splice(position, 0, ...moved);
  const positions = new Map(next.map((field, position) => [field.id, position]));
  const memberships = sections.filter(section => !(section.id === sourceOwner?.id && standaloneFormBlock(section))).map(section => {
    const fieldIds = [...section.fieldIds.filter(id => !movedIds.has(id)), ...(section.id === target?.id ? moved.map(field => field.id) : [])];
    let metadata = section;
    if (block && (section.id === sourceOwner?.id || section.id === target?.id)) {
      const blocks = (section.blocks ?? []).filter(candidate => candidate !== block);
      if (section.id === target?.id) {
        blocks.push(block);
        blocks.sort((left, right) => (positions.get(left.fieldIds[0]) ?? next.length) - (positions.get(right.fieldIds[0]) ?? next.length));
      }
      metadata = withBlocks(section, blocks);
    }
    if (fieldIds.length) return { ...metadata, fieldIds };
    const startIndex = fields.findIndex(field => field.id === (section.fieldIds[0] ?? section.beforeFieldId));
    const before = startIndex < 0 ? undefined : fields.slice(startIndex).find(field => !movedIds.has(field.id))?.id;
    const { beforeFieldId: _anchor, ...empty } = metadata;
    return { ...empty, fieldIds, ...(before ? { beforeFieldId: before } : {}) };
  });
  if (block && !target) memberships.push(standaloneOwner(block));
  return { fields: next, sections: normalizeConversationSections(next, memberships) };
}

/** Reorder a complete group relative to another section while preserving ungrouped fields. */
export function moveBuilderSection(fields: readonly FormField[], sections: readonly ConversationSection[], from: number, to: number): BuilderDefinition {
  if (from === to || !sections[from] || !sections[to]) return { fields: [...fields], sections: [...sections] };
  const source = sections[from]; const target = sections[to];
  const movedIds = new Set(source.fieldIds);
  const moved = fields.filter(field => movedIds.has(field.id));
  const next = fields.filter(field => !movedIds.has(field.id));
  const targetIndices = target.fieldIds.map(id => next.findIndex(field => field.id === id));
  const anchorIndex = target.beforeFieldId ? next.findIndex(field => field.id === target.beforeFieldId) : next.length;
  const position = targetIndices.length ? (from < to ? Math.max(...targetIndices) + 1 : Math.min(...targetIndices)) : Math.max(0, anchorIndex);
  const anchor = next[position]?.id;
  next.splice(position, 0, ...moved);
  const nextSections = sections.map(section => {
    if (section.id === target.id && !section.fieldIds.length && from < to && moved.length) return { ...section, beforeFieldId: moved[0].id };
    if (section.fieldIds.length || !section.beforeFieldId || !movedIds.has(section.beforeFieldId)) return section;
    const oldPosition = fields.findIndex(field => field.id === section.beforeFieldId);
    const beforeFieldId = fields.slice(oldPosition).find(field => !movedIds.has(field.id))?.id;
    const { beforeFieldId: _previousAnchor, ...metadata } = section;
    return { ...metadata, ...(beforeFieldId ? { beforeFieldId } : {}) };
  });
  nextSections.splice(from, 1);
  nextSections.splice(to, 0, { ...source, ...(!moved.length ? { beforeFieldId: anchor } : {}) });
  return { fields: next, sections: normalizeConversationSections(next, nextSections) };
}
